import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios'
import axiosRetry, { IAxiosRetryConfig, isNetworkOrIdempotentRequestError } from 'axios-retry'
import { deleteUserSession } from '../actions/userSession'
import { getAuthData, refreshAuthData } from '../services/session'
import { captureException } from '../sentry-config'

function createError(message: string) {
  const error = new Error(message)
  error.name = 'Gateway Request Failure'
  return error
}

interface AxiosRetryReal extends IAxiosRetryConfig {
  retryCount?: number
}

interface AxiosRequestRetriableConfig extends InternalAxiosRequestConfig {
  'axios-retry'?: AxiosRetryReal
}

const SAFE_HTTP_METHODS = ['get', 'head', 'options']
const IDEMPOTENT_HTTP_METHODS = SAFE_HTTP_METHODS.concat(['put', 'delete'])

function isRetryableAuthenticationError(error: AxiosError): boolean {
  return Boolean(
    error.config &&
      IDEMPOTENT_HTTP_METHODS.indexOf(error.config.method || '') !== -1 &&
      error.code !== 'ECONNABORTED' &&
      error.response?.status === 401
  )
}

class Request {
  axios: AxiosInstance

  constructor() {
    this.axios = axios.create({
      timeout: 60000,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
    axiosRetry(this.axios, {
      retries: 2,
      retryCondition: (error: AxiosError): boolean => {
        const config = error.config as AxiosRequestRetriableConfig | undefined
        if (
          config &&
          config['axios-retry']?.retryCount === 2 &&
          isRetryableAuthenticationError(error)
        ) {
          captureException(new Error('User Session could not be recovered'), {
            extra: {
              name: 'Axios Retry',
            },
          })
          return false
        }

        return isNetworkOrIdempotentRequestError(error) || isRetryableAuthenticationError(error)
      },
    })
    this.axios.interceptors.request.use(this.onRequestSend, this.onRequestError)
    this.axios.interceptors.response.use(this.onResponseSuccess, this.onResponseError)
  }

  setBaseUrl = (baseUrl: string) => {
    this.axios.defaults.baseURL = baseUrl
  }

  onRequestSend = async (config: AxiosRequestConfig): Promise<AxiosRequestRetriableConfig> => {
    const retriableConfig = config as AxiosRequestRetriableConfig
    if (retriableConfig['axios-retry']?.retryCount === 1) {
      await refreshAuthData()
    }

    const { access_token } = await getAuthData()
    retriableConfig.headers['Authorization'] = `Bearer ${access_token}`

    return retriableConfig
  }
  onRequestError = (error: AxiosError): Promise<AxiosError> => Promise.reject(error)
  onResponseSuccess = (response: AxiosResponse) => response?.data
  onResponseError = (error: AxiosError) => {
    // Error
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.status === 401) {
        deleteUserSession()
      } else if (error.response.status === 404) {
        return null
      } else {
        if (error.config) {
          this.reportError(createError(error.message), error.config, error.response)
        }
      }
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      const isNetworkError =
        error.message === 'Network Error' ||
        error.message?.includes('Failed to fetch') ||
        error.code === 'ECONNABORTED'

      if (error.config) {
        this.reportError(
          createError(
            isNetworkError
              ? `Network error: Unable to connect to ${error.config.baseURL}`
              : error.message
          ),
          error.config,
          null
        )
      }
    }
    return Promise.reject(error)
  }

  reportError(
    error: Error,
    request: InternalAxiosRequestConfig | AxiosRequestConfig,
    response?: AxiosResponse | null
  ) {
    if (response?.status === 401 || response?.status === 422) {
      return
    }

    // Truncate response data to prevent Sentry 32KB message limit
    const truncateResponseData = (data: any) => {
      const dataString = JSON.stringify(data)
      const MAX_RESPONSE_SIZE = 10000 // 10KB limit for response data in Sentry
      if (dataString.length > MAX_RESPONSE_SIZE) {
        return {
          truncated: true,
          size: dataString.length,
          data: dataString.substring(0, MAX_RESPONSE_SIZE) + '... [TRUNCATED]',
        }
      }
      return data
    }

    // Remove request body from error config to prevent logging sensitive data
    const sanitizedError = { ...error } as any
    if (sanitizedError.config) {
      sanitizedError.config = {
        ...sanitizedError.config,
        data: '[REDACTED]', // Exclude request body for privacy/security
      }
    }

    captureException(sanitizedError, {
      extra: {
        axios: {
          request: {
            method: request.method,
            url: request.url,
          },
          response: response
            ? {
                status: response.status,
                data: truncateResponseData(response.data),
              }
            : null,
        },
      },
    })
  }

  get = (path: string, config?: AxiosRequestConfig) => this.axios.get(path, config)
  post = (path: string, data?: any, config?: AxiosRequestConfig) =>
    this.axios.post(path, data, config)
  put = (path: string, data?: any, config?: AxiosRequestConfig) =>
    this.axios.put(path, data, config)
  delete = (path: string, config?: AxiosRequestConfig) => this.axios.delete(path, config)
}

export const melodyRequest = new Request()

export const publicRequest = new Request()
